import React from 'react';
import ContentCard from '../Layouts/ContentCard';
import OnboardingSteps from '../Components/Onboarding/OnboardingSteps';
import LoadingSpiner from '../Components/Loader/LoadingSpiner';
import { useSubscriptionComparison } from '../hooks/useSubscriptionComparison';
import { useProfileData } from '../hooks/useProfileData';
import { useListProjectsQuery } from '../api/project';
import { Link } from 'react-router-dom';

export default function Home() {
    // Check for subscription comparison when user lands on dashboard
    useSubscriptionComparison();
    
    // Check onboarding completion status
    const { isLoading: profileLoading, isProfileComplete } = useProfileData();
    const { data: projects, isLoading: projectsLoading } = useListProjectsQuery({});
    
    // Check if any API calls are still loading
    const isLoading = profileLoading || projectsLoading;
    
    // Check if there are any projects
    const hasProjects = projects && projects.length > 0;
    
    // Check if any project has images
    const hasImages = projects && projects.some((project: any) => 
        project.image_count > 0 || (project.images && project.images.length > 0)
    );
    
    // Check if all onboarding steps are complete
    const isOnboardingComplete = isProfileComplete && hasProjects && hasImages;
    
    // Get the 10 most recent projects
    const recentProjects = projects ? [...projects]
        .sort((a: any, b: any) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 10) : [];

    // Show loading state while API calls are in progress
    if (isLoading) {
        return (
            <div className='container mx-auto'>
                <ContentCard title="Dashboard">
                    <div className="flex justify-center items-center py-8">
                        <LoadingSpiner />
                        <span className="ml-2">Loading dashboard...</span>
                    </div>
                </ContentCard>
            </div>
        );
    }

    return (
        <div className='home-page container mx-auto'>
            <ContentCard title="Dashboard" className="dashboard-card">
            </ContentCard>
            

            
            {/* Recent Projects - Only show after onboarding is complete */}
            {isOnboardingComplete && hasProjects && (
                <div className="recent-projects-section">
                    <ContentCard title="Recent Projects" className="recent-projects-card">
                        <div className="recent-projects-table overflow-x-auto">
                            <table className="table w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Name</th>
                                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Images</th>
                                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
                                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Last Modified</th>
                                        <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentProjects.map((project: any) => (
                                        <tr key={project.id} className="recent-project-row hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-700 transition-colors">
                                            <td className="py-3 px-4">
                                                <Link to={`/projects/${project.id}`} className="flex items-center space-x-3 group">
                                                    <div className="project-icon w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded flex items-center justify-center flex-shrink-0">
                                                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                        </svg>
                                                    </div>
                                                    <div className="project-info">
                                                        <div className="project-name font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                                            {project.name}
                                                        </div>
                                                        {project.description && (
                                                            <div className="project-description text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                                                {project.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </Link>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                                                {project.image_count || 0} images
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`inline-block w-2 h-2 rounded-full ${
                                                        project.status === 'active' ? 'bg-green-500' : 
                                                        project.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-400'
                                                    }`}></span>
                                                    <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                                                        {project.status}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                                                {new Date(project.updated_at).toLocaleDateString()}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <div className="flex justify-end space-x-2">
                                                    <Link to={`/projects/${project.id}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium">
                                                        Open
                                                    </Link>
                                                    <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {projects && projects.length > 10 && (
                            <div className="recent-projects-footer mt-6 text-center">
                                <Link to="/projects" className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                                    View All Projects ({projects.length})
                                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        )}
                    </ContentCard>
                </div>
            )}
            
            {/* Welcome and Intro - Only show if onboarding is not complete */}
            {!isOnboardingComplete && (
                <div className="onboarding-section">
                    <ContentCard title="Finish onboarding" className="onboarding-card">
                        <OnboardingSteps />
                    </ContentCard>
                </div>
            )}
        </div>
    );
}